import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  DetailContext,
  FormAutocomplete,
  FormCheckbox,
  FormFileField,
  FormLocalDateField,
  FormLocalDateTimeField,
  FormPanel,
  FormSelect,
  FormTextArea,
  FormTextField,
  eqFilterParams,
  useFormSelector,
  useStaticListSource,
} from '@eas/common-web';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { autocompleteLabelMapper } from '@components/form/misc/label-mappers';
import { InternalDocument } from '@models';
import { Agenda, DecisionState, UIElement } from '@enums';
import { useBriefSummaries } from '../../../dict-brief-summary/dict-brief-summary-api';
import { useInternalDocumentPermission } from '../../../document/document-permission';
import { EsFileFields } from '../../../document/fields/es-file/es-file';

export function InternalDocumentFields({
  enableEditdecision = true,
  createPermission,
}: {
  enableEditdecision?: boolean;
  createPermission?: (element: string) => boolean;
}) {
  const detailCtx = useContext(DetailContext);
  const { agendas, decisionForms } = useContext(StaticDataContext);

  const agendaSource = useStaticListSource(agendas);
  const { agenda, decision, canceledDecision } = useFormSelector(
    (data: InternalDocument) => ({
      agenda: data.agenda,
      decision: data.decision,
      canceledDecision: data.linkedDecision?.state === DecisionState.CANCELED,
    })
  );

  const briefSummaries = useBriefSummaries(
    agenda ? eqFilterParams({ field: 'agenda.id', value: agenda }) : {}
  );
  const decisionFormSource = useStaticListSource(decisionForms);

  /**
   * Permissions
   */
  const { hasPermission } = useInternalDocumentPermission(
    detailCtx?.source?.data as InternalDocument
  );
  const hasFieldPermission = (element: string) =>
    hasPermission(element) || createPermission?.(element) === true;

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__DOCUMENT_INTERNAL__DETAIL__GENERAL_PANEL_TITLE"
            defaultMessage="Obecné"
          />
        }
      >
        <FormTextField
          name="referenceNumber"
          label={
            <FormattedMessage
              id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__REFERENCE_NUMBER"
              defaultMessage="Číslo jednací"
            />
          }
          disabled={!hasFieldPermission(UIElement.InternalDocument.REFERENCE_NUMBER)}
        />
        <FormTextField
          name="jid"
          label={
            <FormattedMessage
              id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__JID"
              defaultMessage="JID"
            />
          }
          disabled={true}
        />

        <FormSelect
          name="agenda"
          label={
            <FormattedMessage
              id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__AGENDA"
              defaultMessage="Agenda"
            />
          }
          valueIsId={true}
          source={agendaSource}
          disabled={!hasFieldPermission(UIElement.InternalDocument.AGENDA)}
        />
        <FormAutocomplete
          name="summary"
          label={
            <FormattedMessage
              id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__SUMMARY"
              defaultMessage="Stručný obsah"
            />
          }
          required
          freeSolo={true}
          autoFocus={true}
          labelMapper={autocompleteLabelMapper}
          source={briefSummaries}
          disabled={!hasFieldPermission(UIElement.InternalDocument.SUMMARY)}
        />
        <FormLocalDateTimeField
          name="createdDate"
          label={
            <FormattedMessage
              id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__CREATED_DATE"
              defaultMessage="Datum vytvoření"
            />
          }
          disabled={
            detailCtx?.isExisting ||
            !hasFieldPermission(UIElement.InternalDocument.CREATED_DATE)
          }
        />
        {agenda === Agenda.PLENARY_OPINION && (
          <FormCheckbox
            name="decision"
            label={
              <FormattedMessage
                id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__DECISION"
                defaultMessage="Rozhodnutí"
              />
            }
            disabled={
              !hasFieldPermission(UIElement.InternalDocument.DECISION) ||
              detailCtx.isExisting
            }
          />
        )}
        {decision && enableEditdecision && (
          <>
            <FormLocalDateField
              name="linkedDecision.decisionDate"
              label={
                <FormattedMessage
                  id="ES__DOCUMENT_OUTGOING__DETAIL__FIELD_LABEL__DECISION_DATE"
                  defaultMessage="Datum rozhodnutí"
                />
              }
              disabled={
                canceledDecision ||
                !hasFieldPermission(UIElement.InternalDocument.DECISION_DATE)
              }
            />
            <FormSelect
              name="linkedDecision.form"
              label={
                <FormattedMessage
                  id="ES__DOCUMENT_OUTGOING__DETAIL__FIELD_LABEL__DECISION_FORM"
                  defaultMessage="Forma rozhodnutí"
                />
              }
              valueIsId={true}
              source={decisionFormSource}
              disabled={
                canceledDecision ||
                !hasFieldPermission(UIElement.InternalDocument.DECISION_FORM)
              }
            />
            <FormFileField
              name="linkedDecision.attachments.fullTextDocx"
              label={
                <FormattedMessage
                  id="ES__DOCUMENT_OUTGOING__DETAIL__FIELD_LABEL__DECISION_FILE"
                  defaultMessage="Rozhodnutí docx"
                />
              }
              accept={['.docx']}
              disabled={
                canceledDecision ||
                !hasFieldPermission(UIElement.InternalDocument.DECISION_DOCX)
              }
            />
          </>
        )}
        <FormTextArea
          name="note"
          label={
            <FormattedMessage
              id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__NOTE"
              defaultMessage="Poznámka"
            />
          }
          disabled={!hasFieldPermission(UIElement.InternalDocument.NOTE)}
        />
        <FormTextField
          type="number"
          name="pageCount"
          label={
            <FormattedMessage
              id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__PAGE_COUNT"
              defaultMessage="Počet listů"
            />
          }
          disabled={!hasFieldPermission(UIElement.InternalDocument.PAGE_COUNT)}
        />
        <FormTextField
          type="number"
          name="order"
          label={
            <FormattedMessage
              id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__ORDER"
              defaultMessage="Pořadí"
            />
          }
          disabled={!hasFieldPermission(UIElement.InternalDocument.ORDER)}
        />
        <FormTextField
          name="barCode"
          label={
            <FormattedMessage
              id="ES__DOCUMENT_INTERNAL__DETAIL__FIELD_LABEL__BAR_CODE"
              defaultMessage="Čárový kód"
            />
          }
          disabled={!hasFieldPermission(UIElement.InternalDocument.BAR_CODE)}
        />
      </FormPanel>
    </>
  );
}

export function General() {
  return (
    <>
      <EsFileFields />
      <InternalDocumentFields />
    </>
  );
}
